/**
 * settingsDialogs — the one way into the Account and Location settings
 * dialogs from anywhere in the app, and the bookkeeping that keeps the URL and
 * the open dialogs in step.
 *
 *  - `openSettings(target)` asks the app shell (App.tsx) to open a settings
 *    dialog over whatever is showing: the user menu's Settings, the Location
 *    dashboard's gear, and ⌘K / header search results all go through it. The
 *    dialog then writes its own address (useSettingsDialogUrl).
 *  - The registry records which settings dialogs are open right now, whoever
 *    opened them (the shell, the Executive dashboard, a Locations list). The
 *    shell reads it before turning an address into a dialog, so Back or
 *    Forward never opens a second copy of a dialog that is already up, and
 *    never tears down one that still has to ask about unsaved changes.
 */
import { parseAccountSettingsLink } from './accountSettingsLink';
import { parseLocationSettingsLink } from './locationSettingsLink';
import { whenSettingsHistoryIdle } from './settingsHistory';

export type SettingsKind = 'account' | 'location';

export type SettingsTarget =
  | { kind: 'account'; accountId: string; tab?: string; subTab?: string; section?: string }
  | { kind: 'location'; locationId: string; accountId?: string; tab?: string; subTab?: string };

export const OPEN_SETTINGS_EVENT = 'tc:openSettings';

/** Ask the app shell to open a settings dialog (see App.tsx). */
export function openSettings(target: SettingsTarget): void {
  window.dispatchEvent(new CustomEvent<SettingsTarget>(OPEN_SETTINGS_EVENT, { detail: target }));
}

export const settingsDialogKey = (kind: SettingsKind, id: string): string => `${kind}:${id}`;

const openDialogs = new Map<string, number>();

export function registerSettingsDialog(key: string): void {
  openDialogs.set(key, (openDialogs.get(key) ?? 0) + 1);
}

// Run when a dialog is no longer open (see whenSettingsPageGone).
const closeWaiters = new Map<string, Array<() => void>>();

export function unregisterSettingsDialog(key: string): void {
  const n = (openDialogs.get(key) ?? 0) - 1;
  if (n > 0) {
    openDialogs.set(key, n);
    return;
  }
  openDialogs.delete(key);
  const waiters = closeWaiters.get(key);
  closeWaiters.delete(key);
  waiters?.forEach((run) => run());
}

export function isSettingsDialogOpen(kind: SettingsKind, id: string): boolean {
  return openDialogs.has(settingsDialogKey(kind, id));
}

// Dialogs that have closed but not yet given their history entry back (a tick
// after unmount, see useSettingsDialogUrl). Until then the address may still
// name them; that is not a link to open them again.
const givingBack = new Map<string, number>();

/** A closed dialog is giving its entry back; call the result once it has. */
export function markSettingsDialogGivingBack(key: string): () => void {
  givingBack.set(key, (givingBack.get(key) ?? 0) + 1);
  let done = false;
  return () => {
    if (done) return;
    done = true;
    const n = (givingBack.get(key) ?? 0) - 1;
    if (n > 0) givingBack.set(key, n);
    else givingBack.delete(key);
  };
}

/**
 * Open, or just closed and still taking its target out of the address. The
 * app shell reads an address that names a settings dialog as a link to open
 * it, unless this says the dialog is (still) handling that address itself.
 */
export function isSettingsDialogHeld(kind: SettingsKind, id: string): boolean {
  const key = settingsDialogKey(kind, id);
  return openDialogs.has(key) || givingBack.has(key);
}

export function isAnySettingsDialogOpen(): boolean {
  return openDialogs.size > 0;
}

/** Tests only. */
export function resetSettingsDialogRegistry(): void {
  openDialogs.clear();
  closeWaiters.clear();
  givingBack.clear();
}

/** Whether the URL names this dialog (kind + id, any tab). */
export function describesSettingsDialog(search: string, kind: SettingsKind, id: string): boolean {
  if (kind === 'account') return parseAccountSettingsLink(search)?.accountId === id;
  return parseLocationSettingsLink(search)?.locationId === id;
}

/**
 * The next value of a shell-held settings link after the address changed
 * (Back, Forward, a reload). A dialog that is already open keeps whatever the
 * shell holds: if the shell opened it, it stays up and decides for itself
 * whether the new address closes it (asking first when something is unsaved);
 * if someone else opened it, the shell does not open a duplicate.
 */
export function nextShellSettingsLink<T>(prev: T | null, fromUrl: T | null, kind: SettingsKind, idOf: (link: T) => string): T | null {
  // Open, or closed and still taking itself out of the address: not a link to (re)open.
  if (fromUrl && isSettingsDialogHeld(kind, idOf(fromUrl))) return prev;
  if (!fromUrl && prev && isSettingsDialogOpen(kind, idOf(prev))) return prev;
  return fromUrl;
}

/**
 * What the user menu's **Settings** opens: the current account's settings
 * when the viewer may read the account, else the current location's when they
 * may read the location, else nothing (the item is hidden). The two checks are
 * the Executive dashboard's (usePermissions canRead for 'accounts' and
 * 'locations'); the caller passes their answers.
 */
export function settingsEntryTarget(scope: {
  canReadAccount: boolean;
  canReadLocation: boolean;
  accountId?: string | null;
  locationId?: string | null;
}): SettingsTarget | null {
  if (scope.canReadAccount && scope.accountId) return { kind: 'account', accountId: scope.accountId };
  if (scope.canReadLocation && scope.locationId) {
    return { kind: 'location', locationId: scope.locationId, ...(scope.accountId ? { accountId: scope.accountId } : {}) };
  }
  return null;
}

/**
 * Leaving the settings page for another part of the app (the main
 * navigation, switching to another location's settings). Each open settings
 * page registers how it lets go: at once when nothing is unsaved, or after the
 * person answers "Unsaved changes" (Save or Discard; Keep editing cancels the
 * move). Pages stack — a location opened from its account — so the most
 * recent one answers first, then the next, and only then does the move run.
 */
export type SettingsLeaver = (proceed: () => void) => void;

const leavers: SettingsLeaver[] = [];
const leaverPages = new WeakMap<SettingsLeaver, { kind: SettingsKind; id?: string }>();

export function registerSettingsLeaver(leaver: SettingsLeaver, kind?: SettingsKind, id?: string): () => void {
  leavers.push(leaver);
  if (kind) leaverPages.set(leaver, { kind, id });
  return () => {
    const i = leavers.lastIndexOf(leaver);
    if (i >= 0) leavers.splice(i, 1);
  };
}

/**
 * A page that let go has gone once it has unmounted AND given its history
 * entry back (the step back off it landed). Only then does the next page get
 * asked, or the move run: a location's settings opened from its account's
 * steps back onto the account's entry, and the account's page has to be
 * still open when that lands (or the address reads as a link to it, and the
 * app opens it again), then give that entry back itself.
 *
 * A page that never named itself in the address (no dialog registered) has
 * nothing to give back: a tick for React's commit is enough. One that does
 * not unmount within a few seconds (a host that never closes it) does not
 * hold the move forever.
 */
const PAGE_GONE_GIVE_UP_MS = 3000;

function whenSettingsPageGone(page: SettingsLeaver, then: () => void): void {
  let finished = false;
  let giveUp: ReturnType<typeof setTimeout> | undefined;
  const finish = () => {
    if (finished) return;
    finished = true;
    clearTimeout(giveUp);
    const i = leavers.lastIndexOf(page);
    if (i >= 0) leavers.splice(i, 1);
    then();
  };
  const where = leaverPages.get(page);
  const key = where?.id ? settingsDialogKey(where.kind, where.id) : null;
  if (!key || !openDialogs.has(key)) {
    // Not in the address (or already gone): React's commit, then any step back.
    setTimeout(() => whenSettingsHistoryIdle(finish), 0);
    return;
  }
  const waiter = () => whenSettingsHistoryIdle(finish);
  const waiters = closeWaiters.get(key) ?? [];
  waiters.push(waiter);
  closeWaiters.set(key, waiters);
  // Gave up waiting (the page never unmounted, or another copy with the same
  // key keeps it registered): stop listening for its close.
  giveUp = setTimeout(() => {
    const list = closeWaiters.get(key);
    const i = list?.indexOf(waiter) ?? -1;
    if (list && i >= 0) list.splice(i, 1);
    if (list?.length === 0) closeWaiters.delete(key);
    finish();
  }, PAGE_GONE_GIVE_UP_MS);
}

// Pages that have let go and are on their way out. A second leave in that
// window (a double click on Dashboard) is ignored: the first one is already
// taking the person there, and asking the same page twice would start a
// second chain (and a second "Unsaved changes" on the page underneath).
const goingPages = new WeakSet<SettingsLeaver>();

export function leaveSettingsThen(proceed: () => void): void {
  const top = leavers[leavers.length - 1];
  if (!top) {
    proceed();
    return;
  }
  if (goingPages.has(top)) return;
  top(() => {
    goingPages.add(top);
    whenSettingsPageGone(top, () => leaveSettingsThen(proceed));
  });
}

/** Which settings page is showing (the most recent one), or null when none is open. */
export function topSettingsKind(): SettingsKind | null {
  return topSettingsPage()?.kind ?? null;
}

/** The settings page showing (kind and id), or null — to reopen it if a move fails. */
export function topSettingsPage(): { kind: SettingsKind; id?: string } | null {
  const top = leavers[leavers.length - 1];
  return top ? (leaverPages.get(top) ?? null) : null;
}

/**
 * Close every open settings page (most recent first, each asking about its
 * unsaved edits) and return to the view they were opened over. False when no
 * settings page is open to answer.
 */
export function leaveAllSettings(): boolean {
  if (leavers.length === 0) return false;
  leaveSettingsThen(() => undefined);
  return true;
}

/** Leave only the most recent settings page (switching it for another), then carry on. */
export function leaveTopSettingsThen(proceed: () => void): void {
  const top = leavers[leavers.length - 1];
  if (!top) {
    proceed();
    return;
  }
  if (goingPages.has(top)) return;
  top(() => {
    goingPages.add(top);
    whenSettingsPageGone(top, proceed);
  });
}

/** Tests only. */
export function resetSettingsLeavers(): void {
  leavers.length = 0;
  sectionShowers.clear();
}

/**
 * Showing a section of a settings page that is already open (the account's
 * Invoicing, asked for from a location's settings stacked on top of it). Each
 * open page registers how it switches to a section; the most recent one wins.
 */
export type AccountSettingsTarget = Extract<SettingsTarget, { kind: 'account' }>;
type SectionShower = (target: AccountSettingsTarget) => void;
const sectionShowers = new Map<string, SectionShower[]>();

export function registerSettingsSectionShower(kind: SettingsKind, id: string, show: SectionShower): () => void {
  const key = settingsDialogKey(kind, id);
  sectionShowers.set(key, [...(sectionShowers.get(key) ?? []), show]);
  return () => {
    const list = (sectionShowers.get(key) ?? []).filter((s) => s !== show);
    if (list.length) sectionShowers.set(key, list);
    else sectionShowers.delete(key);
  };
}

function showOpenAccountSection(target: AccountSettingsTarget): boolean {
  const list = sectionShowers.get(settingsDialogKey('account', target.accountId));
  const show = list?.[list.length - 1];
  if (!show) return false;
  show(target);
  return true;
}

/**
 * Go to an account's settings on one section from a settings page (a
 * location's "Open the account's Invoicing"):
 *
 *  - the account's page is open underneath (the location was opened from
 *    it): leave only the page on top (it asks about its own unsaved edits),
 *    then switch the account's page to the section;
 *  - otherwise: leave every open settings page, then open the account's.
 *
 * A plain openSettings() from a settings page does nothing: the app shell
 * opens one settings page at a time and ignores a request while one is up.
 */
export function goToAccountSettings(target: AccountSettingsTarget): void {
  const top = topSettingsPage();
  if (top?.kind === 'account' && top.id === target.accountId) {
    if (!showOpenAccountSection(target)) openSettings(target);
    return;
  }
  if (isSettingsDialogOpen('account', target.accountId)) {
    leaveTopSettingsThen(() => {
      if (!showOpenAccountSection(target)) openSettings(target);
    });
    return;
  }
  leaveSettingsThen(() => openSettings(target));
}
